import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2, ChevronRight, Clock, FileText, Shield, Trophy, Users } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { num: 1, label: "Registrasi" },
  { num: 2, label: "Isi Formulir" },
  { num: 3, label: "Upload Dokumen" },
  { num: 4, label: "Verifikasi" },
  { num: 5, label: "Pengumuman" },
];

const PROGRAMS = ["MIPA Riset", "IPS Terapan", "Bahasa & Budaya"];
const STATUS_LABEL = { baru: "Baru", diverifikasi: "Diverifikasi", diproses: "Diproses", diterima: "Diterima", tidak_diterima: "Tidak Diterima", daftar_ulang: "Daftar Ulang" };

export default function PPDB() {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [checkReg, setCheckReg] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [form, setForm] = useState({
    full_name: "", nisn: "", birth_place: "", birth_date: "", gender: "Laki-laki",
    previous_school: "", address: "", parent_name: "", phone: "", email: "", program_choice: PROGRAMS[0], documents: [],
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/ppdb/apply", form);
      setSubmitted(data);
      toast.success("Pendaftaran berhasil! Simpan nomor pendaftaran Anda.");
      setShowForm(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Gagal mengirim pendaftaran. Periksa isian Anda.");
    }
  };

  const doCheck = async () => {
    if (!checkReg) return;
    try {
      const { data } = await api.get(`/ppdb/check/${checkReg}`);
      setCheckResult(data);
    } catch { toast.error("Nomor pendaftaran tidak ditemukan"); setCheckResult(null); }
  };

  return (
    <div data-testid="ppdb-page">
      <PageHero
        eyebrow="Penerimaan Peserta Didik Baru"
        title="PPDB 2026/2027 — Daftar Sekarang"
        subtitle="Jadilah bagian dari generasi GARAM — berakhlak, unggul, responsif, dan mandiri. Pendaftaran online cepat, mudah, dan aman."
        image="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=2000&q=80"
      />

      {submitted && (
        <section data-testid="ppdb-success" className="py-10 bg-green-50 border-b border-green-200">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="card-elevated p-8 border-2 border-green-500">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
              <h3 className="font-serif text-2xl font-bold text-[#0B1D3A] mt-4">Pendaftaran Berhasil!</h3>
              <p className="mt-2 text-slate-600">Simpan nomor pendaftaran Anda untuk cek status di kemudian hari:</p>
              <div className="mt-4 p-4 rounded-lg bg-slate-900 text-amber-400 font-mono text-xl tracking-wider">{submitted.registration_number}</div>
              <p className="mt-3 text-sm text-slate-500">Status saat ini: <span className="font-semibold text-[#0B1D3A]">Baru</span></p>
            </div>
          </div>
        </section>
      )}

      {/* WHY */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Mengapa Memilih Kami</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Keunggulan SMAS YPK 2 Biak</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Trophy, title: "Prestasi Terbukti", desc: "48+ prestasi akademik, olahraga, dan seni dari Kabupaten hingga Nasional." },
              { icon: Users, title: "Guru Profesional", desc: "32 pendidik berdedikasi, 88% S1 dan 92% tersertifikasi." },
              { icon: Shield, title: "Lingkungan Aman", desc: "Anti-tawuran, anti-narkoba, dengan nilai Kristiani yang kuat." },
              { icon: FileText, title: "Akreditasi A", desc: "Pengakuan resmi BAN-S/M atas kualitas sekolah." },
            ].map((f) => {
              const I = f.icon;
              return (
                <div key={f.title} className="card-elevated p-6">
                  <I className="w-9 h-9 text-amber-500" />
                  <div className="font-serif text-lg font-bold text-[#0B1D3A] mt-4">{f.title}</div>
                  <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Alur Pendaftaran</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">5 Langkah Mudah</h2>
          </div>
          <div className="relative grid md:grid-cols-5 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.num} className="card-elevated p-6 relative text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-amber-500 text-[#0B1D3A] font-serif font-black text-xl flex items-center justify-center">{s.num}</div>
                <div className="font-serif font-bold text-[#0B1D3A] mt-4">{s.label}</div>
                {i < STEPS.length - 1 && <ChevronRight className="hidden md:block absolute right-[-14px] top-1/2 -translate-y-1/2 w-6 h-6 text-slate-300" />}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button data-testid="ppdb-open-form" onClick={() => { setShowForm(true); setTimeout(() => document.getElementById("ppdb-form")?.scrollIntoView({ behavior: "smooth" }), 50); }}
              size="lg" className="bg-[#0B1D3A] hover:bg-[#132D58] text-white rounded-full px-8 h-12 shadow-xl">
              Daftar PPDB Online
            </Button>
          </div>
        </div>
      </section>

      {/* FORM */}
      {showForm && (
        <section id="ppdb-form" data-testid="ppdb-form-section" className="py-16 lg:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <h2 className="font-serif text-3xl font-bold text-[#0B1D3A] text-center">Formulir Pendaftaran</h2>
            <form onSubmit={submit} className="mt-10 card-elevated p-8 space-y-5">
              {[
                ["full_name", "Nama Lengkap", "text", true],
                ["nisn", "NISN", "text", true],
                ["birth_place", "Tempat Lahir", "text", true],
                ["birth_date", "Tanggal Lahir", "date", true],
                ["previous_school", "Asal Sekolah", "text", true],
                ["parent_name", "Nama Orang Tua / Wali", "text", true],
                ["phone", "Nomor Telepon", "tel", true],
                ["email", "Email", "email", true],
              ].map(([k, l, t, req]) => (
                <div key={k}>
                  <Label htmlFor={k}>{l}</Label>
                  <Input id={k} data-testid={`ppdb-${k}`} type={t} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
                </div>
              ))}
              <div>
                <Label>Jenis Kelamin</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="ppdb-gender" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                    <SelectItem value="Perempuan">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pilihan Program</Label>
                <Select value={form.program_choice} onValueChange={(v) => setForm({ ...form, program_choice: v })}>
                  <SelectTrigger data-testid="ppdb-program" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="address">Alamat</Label>
                <Textarea id="address" data-testid="ppdb-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" rows={3} />
              </div>
              <Button type="submit" data-testid="ppdb-submit" size="lg" className="w-full bg-amber-500 hover:bg-amber-600 text-[#0B1D3A] font-bold h-12 rounded-full">Kirim Pendaftaran</Button>
            </form>
          </div>
        </section>
      )}

      {/* CHECK STATUS */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Cek Status</div>
          <h2 className="font-serif text-3xl font-bold text-[#0B1D3A]">Periksa Status Pendaftaran</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Input data-testid="ppdb-check-input" placeholder="Masukkan nomor pendaftaran" value={checkReg} onChange={(e) => setCheckReg(e.target.value)} className="h-12 rounded-full" />
            <Button data-testid="ppdb-check-btn" onClick={doCheck} className="h-12 rounded-full bg-[#0B1D3A] hover:bg-[#132D58] px-8">Cek Status</Button>
          </div>
          {checkResult && (
            <div data-testid="ppdb-check-result" className="mt-6 card-elevated p-6 text-left">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Nama</div>
              <div className="font-serif text-xl font-bold text-[#0B1D3A]">{checkResult.full_name}</div>
              <div className="mt-3 text-xs text-slate-500 uppercase tracking-wider">Program</div>
              <div className="font-semibold">{checkResult.program_choice}</div>
              <div className="mt-3 text-xs text-slate-500 uppercase tracking-wider">Status</div>
              <div className="mt-1 inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-700 font-bold text-sm">{STATUS_LABEL[checkResult.status] || checkResult.status}</div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
